import React, { Fragment, useContext } from 'react'

import { DefaultMediaWizardLayout } from './DefaultMediaWizardLayout'
import { Carousel, CarouselItem } from 'components/Carousel'
import { NewsHeader, NewsLink, WizardNewsSummary } from 'components/News'
import { LinkButton, LinkSection, LinkSections, vodafoneExternalLinks } from 'components/Links'

import { useDocumentTitle } from '@percept/hooks'

import { UserPrivilegeContext } from '@percept/app-components'

import { get } from 'lodash-es'

import carouselWhite2 from '../images/carousel-backgrounds/carousel-white-2.jpg'
import carouselGrey2 from '../images/carousel-backgrounds/carousel-grey-2.jpg'
import carouselBlack2 from '../images/carousel-backgrounds/carousel-black-2.jpg'

// import infoGraphicCarbonDioxide from '../images/infographics/5.4-tons-light-bg.svg'
import infoGraphicSustainability from '../images/infographics/globe-green.svg'
// import infoGraphic46Percent from '../images/infographics/46-percent-dark-bg.svg'

import infoGraphicMacbookMQD from '../images/infographics/macbook-mqd.png'
import infoGraphicMacbookCarbonCalculator from '../images/infographics/macbook-carbon-calculator.png'
import infoGraphicIpadCreativeX from '../images/infographics/ipad-creativex.png'
import infoGraphicProviders from '../images/infographics/provider-logos.svg'
import infoGraphicCalculator from '../images/infographics/calculator-light-bg.svg'
import infoGraphicMediaAcademy from '../images/infographics/media-academy-infographic.png'

import { AnimationDigitalMedia } from 'animations'

import { InfographicCQS } from 'components/StaticInfographics'
import { Grid, makeAppStyles, useBreakpoint, vodafonePalette } from '@percept/mui'
import { VODAFONE_GLOBAL_ID, vodafoneMarkets } from 'vodafoneMarkets'
import { MarketDisplayLabel } from 'components/MarketDisplay'
import { produceKeyedMapping } from '@percept/utils'

const marketsByName = produceKeyedMapping(vodafoneMarkets, 'name')

const carouselItems: CarouselItem[] = [
  {
    title: 'Media that works',
    description: (
      <Fragment>
        Track, review and analyse your digital media, to maximise impact and reduce wastage.
        Sharing best practices across search, social and programmatic formats.
      </Fragment>

    ),
    textColor: 'black',
    backgroundImageSrc: carouselWhite2,
    // infoGraphicSrc: infoGraphicDigitalMedia,
    GraphicComponent: AnimationDigitalMedia,
  },
  // {
  //   title: 'Did you know strong creative performance drives profitability by 46%?',
  //   description: (
  //     <Fragment>
  //       Get the most from your creative assets, with best practice guidance across Meta, YouTube,
  //       TikTok, Adform and DV360.
  //     </Fragment>
  //   ),
  //   textColor: 'white',
  //   backgroundImageSrc: carouselBlack2,
  //   infoGraphicSrc: infoGraphic46Percent,
  // },
  {
    title: (
      <>
        Maximize the impact of every creative decision in digital, through{' '}
        <span style={{color: vodafonePalette.red}}>
          creative pre-flight testing
        </span>
        {' '}and hitting 80% Creative Quality Score target
      </>
    ),
    titleSize: 0.825,
    backgroundImageSrc: carouselBlack2,
    textColor: 'white',
    infoGraphicContent: <InfographicCQS />,
  },
  {
    title: 'Did you know that Vodafone media emitted 16,958 tCO2e in 2023?',
    description: (
      <Fragment>
        That&apos;s equivalent to roughly 3,800 round trip flights between London and Cape Town.
        Measure and minimise your media carbon emissions, to support Vodafone&apos;s commitment to being
        net zero by 2040.
      </Fragment>
    ),
    textColor: 'black',
    backgroundImageSrc: carouselGrey2,
    infoGraphicSrc: infoGraphicSustainability,
  },
]


const useStyles = makeAppStyles( theme => ({
  discoveryGrid: {
    padding: theme.spacing(0, 4),
    maxWidth: '65%',
    [theme.breakpoints.down('lg')]: {
      padding: theme.spacing(0, 2),
      maxWidth: '85%',
    },
    [theme.breakpoints.down('md')]: {
      padding: theme.spacing(0, 1),
      maxWidth: '100%',
    },
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(0),
      maxWidth: '80%',
    },
    [theme.breakpoints.down('xs')]: {
      padding: theme.spacing(0),
      maxWidth: '100%',
    }
  },
  discoveryGridItem: {
    display: 'flex',
    '&:nth-child(odd)': {
      [theme.breakpoints.up('md')]: {
        justifyContent: 'flex-end',
      },
    },
    '&:nth-child(even)': {
      [theme.breakpoints.up('md')]: {
        justifyContent: 'flex-start',
      },
    },
  },
  discoveryLink: {
    width: '100%',
  },
}))



export const Optimisation = (): JSX.Element => {

  useDocumentTitle({ paths: ['Optimisation'] })

  const privileges = useContext(UserPrivilegeContext)
  const default_org_unit_id = get(privileges.user, 'default_org_unit_id', null)

  const breakpoint = useBreakpoint()

  const classes = useStyles()

  const isMobile = breakpoint === 'xs' || breakpoint === 'sm'

  const linkSections: LinkSection[] = [
    {
      title: 'Media Quality Dashboard',
      description: (
        <Fragment>
          Track, review and analyse your digital media performance to maximise impact
          and reduce wastage across Search (Google), Social (Meta and YouTube) and
          Programmatic (Adform and DV360). Get inspired by what other Vodafone markets
          are doing.
        </Fragment>
      ),
      globalPrivileges: ['structuralReporting.viewAny', 'performanceOverview.viewAny'],
      imageSrc: infoGraphicMacbookMQD,
      path: default_org_unit_id ? (
        `/dashboards/${default_org_unit_id}`
      ) : undefined,
      openInNewWindow: true,
    },
    {
      title: 'Total Discovery Dashboard',
      description: (
        <Fragment>
          Track how Vodafone appears across AI platforms (ChatGPT, Gemini, etc.) and
          measure LLM-driven discovery beyond clicks. Access real-time insights on visibility,
          sentiment, citations and ranking to turn AI visibility into actionable improvements
          across content, SEO/GEO and media strategies.
        </Fragment>
      ),
      imageContent: (
        <Grid
          container
          classes={{ root: classes.discoveryGrid }}
          spacing={
            isMobile ?
              1 :
              breakpoint === 'xl' ?
                3 :
                2
          }>
          { vodafoneExternalLinks.discoveryDashboardsByMarket.map( ({ market, url }) => {
            const marketObject = marketsByName[market]
            if( !marketObject ) return null
            return (
              <Grid
                item
                xs={12}
                md={6}
                key={marketObject.id}
                classes={{ root: classes.discoveryGridItem }}>
                <LinkButton
                  size='small'
                  fullWidth
                  to={url}
                  openInNewWindow
                  className={classes.discoveryLink}>
                  <MarketDisplayLabel {...marketObject} />
                </LinkButton>
              </Grid>
            )
          })
          }
        </Grid>
      ),
      suppressPlaceholderLink: true,
    },
    /* NOTE- Creative Quality Dashboard has been replaced with Total Discovery Dashboard */
    // {
    //   title: 'Creative Quality Dashboard',
    //   description: (
    //     <Fragment>
    //       Access your creative quality scores and spend rates across Meta, YouTube, TikTok
    //       and Programmatic (Adform and DV360). Using real-time data to optimise your
    //       campaigns and share learnings across our markets.
    //     </Fragment>
    //   ),
    //   imageSrc: infoGraphicIpadCreativeX,
    //   path: vodafoneExternalLinks.creativeX,
    //   openInNewWindow: true,
    // },
    {
      title: 'Media Carbon Calculator',
      description: (
        <Fragment>
          <p>
            Measure and minimise carbon emissions across your media activities, by channel,
            supplier and format. Share learnings across markets to inspire others to reduce
            their carbon footprint.
          </p>
          <p>
            Get inspired by how other Vodafone markets are reducing their carbon footprint.
          </p>
        </Fragment>
      ),
      imageSrc: infoGraphicMacbookCarbonCalculator,
      path: vodafoneExternalLinks.carbonCalculator,
      openInNewWindow: true,
    },
    /* NOTE - Partnerships application has been disabled */
    // {
    //   title: 'Digital Media Partnerships',
    //   description: (
    //     <Fragment>
    //       Fuel your ongoing media growth with access to JBP strategies and benchmarking scorecards.
    //       Explore the latest news, resources and webinars to support your campaign performance.
    //     </Fragment>
    //   ),
    //   globalPrivileges: ['partnerships.viewAny'],
    //   path: '/wizard/optimisation/partnerships',
    //   openInNewWindow: true,
    //   imageSrc: infoGraphicProviders,
    // },
    {
      title: 'Sponsorship Evaluation Calculator',
      description: (
        <Fragment>
          Drive better commercial decisions across digital and real estate, with our bespoke asset
          inventory and evaluation tool.
        </Fragment>
      ),
      globalPrivileges: ['sponsorships.viewAny'],
      path: '/wizard/optimisation/sponsorships',
      openInNewWindow: true,
      imageSrc: infoGraphicCalculator,
    },
    // NOTE - temporarily hidden for individual user previews via direct link
    {
      title: 'Media Academy',
      description: (
        <Fragment>
          Visit our central hub for all past and upcoming webinars, including presentation decks, replay links,
          playbooks, and whitepapers. Content is organised under four pillars: Enable, Expand, Extract, and Martech,
          making it easy to find relevant resources and stay updated on future sessions to grow our impact.
        </Fragment>
      ),
      orgPrivileges: ['vfMediaWizard.mediaAcademy.view'],
      orgUnitId: VODAFONE_GLOBAL_ID,
      imageSrc: infoGraphicMediaAcademy,
      path: '/wizard/optimisation/media-academy',
      openInNewWindow: true,
    },
  ]

  return (
    <DefaultMediaWizardLayout
      breadcrumbs={[
        {
          path: '/wizard/optimisation',
          name: 'Optimisation',
        },
      ]}>

      <Carousel items={carouselItems} />

      <LinkSections sections={linkSections} />

      <NewsHeader />
      
      <WizardNewsSummary />

      <NewsLink />

    </DefaultMediaWizardLayout>
  )
}
